/**
 * Clerk Auth Provider
 *
 * Provides UnifiedAuthContext using Clerk authentication.
 * Use this provider for regular (non-embedded) routes.
 */
import { ReactNode, useMemo } from 'react';
import { isWeekStartsOn } from '@aica/core';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { UnifiedAuthProvider } from './UnifiedAuthContext';

export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const {
    selectedOrgId,
    selectedOrg,
    globalRole,
    isLoading: roleLoading,
    isOrgSwitching,
  } = useRole();
  const { apiClient } = useApiClient();
  // From the `/users/me` roster (#864): every member loads it, whatever the role.
  const rosterWeekStartsOn = selectedOrg?.weekStartsOn;
  const weekStartsOn = isWeekStartsOn(rosterWeekStartsOn) ? rosterWeekStartsOn : null;

  const value = useMemo(
    () => ({
      apiClient,
      orgId: selectedOrgId,
      role: globalRole,
      isLoading: roleLoading,
      isOrgSwitching,
      error: null, // Clerk handles errors differently
      weekStartsOn,
    }),
    [apiClient, selectedOrgId, globalRole, roleLoading, isOrgSwitching, weekStartsOn]
  );

  return <UnifiedAuthProvider value={value}>{children}</UnifiedAuthProvider>;
}
